import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Dialog } from '@mui/material';
import FileSaver from 'file-saver';
import Konva from 'konva';
import { toast } from 'react-toastify';
import API from 'api/API';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import sleep from 'utils/helpers/sleep.helpers';
import ActionBar from './molecules/ActionBar';
import ReferenceImageStage, { Tag } from './molecules/ImageGeneratorStage';
import { ModalContainer } from './ReferenceImageModal.styles';

interface ReferenceImageGeneratorProps {
  imageUrl?: string;
  modalOpen: boolean;
  setClosed: () => void;
  originalImage: string;
  existingTags?: Tag[];
  onSaveTaggedImage: (tags: Tag[], taggedImage: string, originalImage: string) => void;
}

const STAGE_TO_WINDOW_RATIO = 0.8;
const MODAL_BORDER = 10;
const TAG_OFFSET = 10;
const TAG_BORDER = 50;

const ReferenceImageGenerator: FC<ReferenceImageGeneratorProps> = ({
  imageUrl,
  modalOpen,
  setClosed,
  originalImage,
  existingTags,
  onSaveTaggedImage,
}) => {
  const [language] = useLanguage();

  const stageRef = useRef<Konva.Stage>(null);
  const [stageWidth, setStageWidth] = useState(window.innerWidth * STAGE_TO_WINDOW_RATIO);
  const [stageHeight, setStageHeight] = useState(window.innerHeight * STAGE_TO_WINDOW_RATIO);
  const [tags, setTags] = useState<Tag[]>(existingTags ?? []);
  const [actualHeight, setActualHeight] = useState(stageHeight);
  const [actualWidth, setActualWidth] = useState(stageWidth);

  const [selectedTag, setSelectedTag] = useState<number>();

  const resizeStage = () => {
    setStageWidth(window.innerWidth * STAGE_TO_WINDOW_RATIO);
    setStageHeight(window.innerHeight * STAGE_TO_WINDOW_RATIO);
  };

  const addNewTag = () => {
    const tag = tags.length;

    const heightLimit = actualHeight - TAG_BORDER;
    const widthLimit = actualWidth - TAG_BORDER;
    const nextY = (tag + 1) * TAG_OFFSET;
    const nextX = (tag + 1) * TAG_OFFSET;

    const newTag = {
      tag,
      x: nextX % widthLimit,
      y: nextY % heightLimit,
      text: (tag + 1).toString(),
    };
    setTags([...tags, newTag]);
  };

  const removeLastTag = () => {
    setTags(tags.slice(0, -1));
  };

  const onImageLoadError = () => {
    toast.error(language.ReferenceImageModal.imageLoadError);
    setClosed();
  };

  const updateImageSize = (height: number, width: number) => {
    setActualHeight(height + MODAL_BORDER); // + 10 to account for border
    setActualWidth(width + MODAL_BORDER);
  };

  // Updates a single tag in the array, matched on the `tag` input
  const updateTag = (tag: number, x: number, y: number, text: string, width: number, height: number) => {
    setTags(tags.map(t => (t.tag === tag ? { tag, x, y, width, height, text } : t)));
  };

  const downloadStageImage = async () => {
    const stage = stageRef.current;
    if (stage) {
      if (selectedTag !== undefined) {
        setSelectedTag(undefined);
        // wait 100ms to allow for deselection
        await sleep(100);
      }

      const uri = stage.toDataURL();
      const imageNameSplits = originalImage.split('tray_reference_images/');
      const name = imageNameSplits[imageNameSplits.length - 1];

      if (uri) {
        FileSaver.saveAs(uri, `TAGGED_${name}`);
      } else {
        toast.error(language.ReferenceImageModal.imageDownloadError);
      }
    }
  };

  const handleOnSaveAndSubmit = async () => {
    try {
      if (stageRef.current) {
        if (selectedTag !== undefined) {
          setSelectedTag(undefined);
          // wait 100ms to allow for deselection
          await sleep(100);
        }
        const uri = stageRef.current.toDataURL();
        onSaveTaggedImage(tags, uri, originalImage);
      }
    } catch (error) {
      API.errorHandler(error, language.ReferenceImageModal.imageSaveError);
    }
  };

  // Watch the window resizing so we update area dynamically
  useLayoutEffect(() => {
    resizeStage();
    window.addEventListener('resize', resizeStage);
  }, []);

  // Update the tags if they are updated
  useEffect(() => {
    setTags(existingTags ?? []);
  }, [existingTags]);

  return (
    <Dialog open={modalOpen} onClose={() => setClosed()} PaperProps={{ sx: { maxWidth: stageWidth } }}>
      {imageUrl ? (
        <ModalContainer>
          <ActionBar
            addNewTag={addNewTag}
            removeLastTag={removeLastTag}
            downloadStage={downloadStageImage}
            onSaveAndSubmit={handleOnSaveAndSubmit}
            exit={setClosed}
            height={actualHeight}
          />
          <ReferenceImageStage
            imageUrl={imageUrl}
            maxWidth={stageWidth - 240}
            maxHeight={stageHeight}
            stageRef={stageRef}
            tags={tags}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            updateImageSize={updateImageSize}
            onImageLoadError={onImageLoadError}
            updateTag={updateTag}
          />
        </ModalContainer>
      ) : (
        <PageSpinner />
      )}
    </Dialog>
  );
};

export default ReferenceImageGenerator;
